/**
 * Defensive patch for React's "Cannot read properties of null (reading
 * 'removeChild'/'insertBefore')" crash when third-party scripts mutate the
 * DOM inside React-managed subtrees.
 *
 * Known offenders on this site: Perfectus Labs / Cognos AI dynamic
 * optimization (rewrites text nodes), GHL external tracking, CallRail swap,
 * and any translation extension. Same class of bug that Google Translate
 * causes on every React site.
 *
 * We wrap Node.prototype.removeChild and insertBefore so that if React
 * asks to remove a child that is no longer parented to `this`, we fall
 * back to a real removal or a no-op instead of throwing. Behaviour for
 * healthy calls is unchanged.
 */
export function installDomMutationGuards() {
  if (typeof window === "undefined") return;
  const w = window as unknown as { __ctrDomGuardsInstalled?: boolean };
  if (w.__ctrDomGuardsInstalled) return;
  w.__ctrDomGuardsInstalled = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child && child.parentNode !== this) {
      if (child.parentNode) {
        try {
          child.parentNode.removeChild(child);
        } catch {
          /* noop */
        }
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  } as typeof Node.prototype.removeChild;

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      try {
        return originalInsertBefore.call(this, newNode, null) as T;
      } catch {
        return newNode;
      }
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  } as typeof Node.prototype.insertBefore;
}
